import React, { MouseEvent, useCallback, useMemo, useState } from 'react';

import { Icon } from '@rbilabs/component-library';
import { useIntl } from 'react-intl';
import { useMatch, useNavigate } from 'react-router-dom';

import OrderCancelledModal from 'components/order-cancelled-modal';
import {
  DeliveryStatus,
  IUserOrderFragment,
  RbiOrderStatus,
  useGetUserOrdersQuery
} from 'generated/rbi-graphql';
import useDialogModal from 'hooks/use-dialog-modal';
import {
  STATUS_MESSAGES,
  STATUS_PERCENTS
} from 'pages/order-confirmation/delivery/delivery-progress';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { ServiceMode, useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';
import { useUIContext } from 'state/ui';
import { routes } from 'utils/routing';

import {
  BottomServiceVariants,
  IBottomServiceModeDetail,
  IOrderInProgress,
  IOrderInProgressResult
} from '../types';
import { deliveryCompleteTimeout, postOrderTimeout } from '../utils';

const DELIVERY_COMPLETED = [
  DeliveryStatus.ORDER_DROPPED_OFF,
  DeliveryStatus.ORDER_ABANDONED,
  DeliveryStatus.ORDER_CANCELLED
];

const ORDER_PROGRESS_POLLING_DURATION_IN_MS = 10000;
const DELIVERY_COMPLETED_TIMEOUT_IN_MINUTES = 10;
const RECENT_ORDER_UPDATED_TIMEOUT_IN_MINUTES = 60;
const CURBSIDE_UPDATED_TIMEOUT_IN_MINUTES = 10;

export const getDeliveryHeading = ({ deliveryStatus }: { deliveryStatus: DeliveryStatus }) => {
  const deliveryProgress = STATUS_PERCENTS[deliveryStatus];
  if (deliveryProgress <= STATUS_PERCENTS[DeliveryStatus.ORDER_CREATED]) {
    return 'orderPlaced';
  }
  if (deliveryProgress < STATUS_PERCENTS[DeliveryStatus.ORDER_PICKED_UP]) {
    return 'deliveryOrderBeingPrepared';
  }
  if (deliveryProgress < STATUS_PERCENTS[DeliveryStatus.ORDER_DROPPED_OFF]) {
    return 'deliveryDriverEnroute';
  }
  return 'deliveryDelivered';
};

export const isPollingForDelivery = (
  recentOrder: IUserOrderFragment | null | undefined,
  isDeliveryOrderCompleted: boolean
) => {
  if (
    recentOrder?.delivery &&
    !isDeliveryOrderCompleted &&
    !DELIVERY_COMPLETED.includes(recentOrder.delivery.status)
  ) {
    return true;
  }
  return false;
};

export const orderInProgress = ({
  recentOrder,
  curbsidePickupOrderId,
  serviceMode,
  isDeliveryOrderCompleted
}: IOrderInProgress): IOrderInProgressResult | null => {
  // Timeout time of 60 minutes of recent orders:
  // - ServiceMode.TAKEOUT
  // - ServiceMode.DRIVE_THRU
  // - ServiceMode.EAT_IN
  // Timeout time of 10 minutes from order placed:
  // - ServiceMode.CURBSIDE
  const isPickupTimedOut = () => {
    const pickupTimeout =
      serviceMode === ServiceMode.CURBSIDE
        ? CURBSIDE_UPDATED_TIMEOUT_IN_MINUTES
        : RECENT_ORDER_UPDATED_TIMEOUT_IN_MINUTES;

    return (
      recentOrder &&
      !recentOrder?.delivery &&
      postOrderTimeout(recentOrder?.updatedAt, pickupTimeout)
    );
  };

  // ServiceMode.DELIVERY - ordered
  if (recentOrder?.delivery && !isDeliveryOrderCompleted) {
    const deliveryStatus = recentOrder.delivery.status;
    const headingId = getDeliveryHeading({ deliveryStatus });
    const detailsId = STATUS_MESSAGES[deliveryStatus];
    const buttonContentTextId =
      deliveryStatus === DeliveryStatus.ORDER_DROPPED_OFF ? 'viewDetails' : 'track';
    return {
      headingId,
      detailsId,
      buttonContentTextId,
      buttonContentIcon: null,
      icon: <Icon icon="markerFilled" color="white" width="1.75rem" height="1.75rem" />,
      variant: BottomServiceVariants.IN_PROGRESS
    };
  }
  // PICKUP - ordered
  if (recentOrder && isPickupTimedOut()) {
    return {
      headingId: 'orderPlaced',
      detailsId: 'yourOrderIsBeingPrepared',
      icon: <Icon icon="restaurant" color="white" width="1.75rem" height="1.75rem" />,
      buttonContentTextId: 'viewDetails',
      buttonContentIcon: null,
      variant: BottomServiceVariants.IN_PROGRESS
    };
  }
  // ServiceMode.CURBSIDE - Before order is placed from selecting "I'm here!"
  if (curbsidePickupOrderId) {
    return {
      headingId: 'serviceModeDetailsCurbsideHeader',
      detailsId: 'headToRestaurant',
      icon: <Icon icon="restaurant" color="white" width="1.75rem" height="1.75rem" />,
      buttonContentTextId: 'confirmArrival',
      buttonContentIcon: null,
      variant: BottomServiceVariants.IN_PROGRESS
    };
  }
  return null;
};

export const useBottomServiceModeDetails = (): IBottomServiceModeDetail => {
  const {
    serviceMode,
    deliveryAddress,
    calculateCartTotal,
    canUserCheckout,
    curbsidePickupOrderId
  } = useOrderContext();
  const { store } = useStoreContext();
  const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const { formatCurrencyForLocale } = useUIContext();
  const { formatMessage } = useIntl();
  const { logEvent } = useMParticleContext();
  const { isAuthenticated } = useAuthContext();
  const isAuth = isAuthenticated();
  const [hasSeenCancelModal, setHasSeenCancelModal] = useState(false);
  const [CancellationModal, openCancellationModal] = useDialogModal({
    Component: OrderCancelledModal,
    onDismiss: () => setHasSeenCancelModal(true)
  });

  const isOnMenuPage = useMatch({
    path: routes.menu,
    end: false
  });
  const navigate = useNavigate();
  const { data, startPolling, stopPolling } = useGetUserOrdersQuery({
    variables: {
      limit: 1,
      orderStatuses: [
        RbiOrderStatus.INSERT_SUCCESSFUL,
        RbiOrderStatus.UPDATE_SUCCESSFUL,
        RbiOrderStatus.REFUND_SUCCESSFUL
      ]
    }
  });
  const cartComponent = useMemo(() => formatCurrencyForLocale(calculateCartTotal()), [
    calculateCartTotal,
    formatCurrencyForLocale
  ]);
  const handleCartClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      logEvent(CustomEventNames.BOTTOM_SERVICE_CART, EventTypes.Other, {
        'Service Mode': serviceMode
      });
      isAuth ? navigate(routes.cart) : navigate(routes.serviceMode);
    },
    [isAuth, logEvent, navigate, serviceMode]
  );
  const handleServiceModeChange = useCallback(() => {
    logEvent(CustomEventNames.BOTTOM_SERVICE_MODE, EventTypes.Other, {
      'Service Mode': serviceMode
    });
    navigate(routes.serviceMode);
  }, [logEvent, navigate, serviceMode]);
  const handleNavigateToOrderConfirmation = useCallback(
    (rbiOrderId: string) => () => {
      logEvent(CustomEventNames.BOTTOM_SERVICE_MODE, EventTypes.Other, {
        'Service Mode': serviceMode,
        'Order Id': rbiOrderId
      });
      navigate(`${routes.orderConfirmation}/${rbiOrderId}`);
    },
    [logEvent, navigate, serviceMode]
  );
  // We don't want to block user from ordering when there is existing
  // order in progress. We will always shows default bottom service mode
  // on menu page
  const recentOrder = data?.userOrders?.orders?.[0];

  // This will change the display of BSM after the order has been placed
  // and delivery is being tracked, a waiting that pickup from curbside.
  // Any refunds will not block users from making a new order
  if (!isOnMenuPage && recentOrder?.status !== RbiOrderStatus.REFUND_SUCCESSFUL) {
    // Completed delivery order is when status is completed and
    // exceeded 10 minutes after order is dropped off (in order for user to view the order)
    const isDeliveryOrderCompleted =
      !!recentOrder?.delivery &&
      DELIVERY_COMPLETED.includes(recentOrder.delivery.status) &&
      (hasSeenCancelModal ||
        deliveryCompleteTimeout(recentOrder?.updatedAt, DELIVERY_COMPLETED_TIMEOUT_IN_MINUTES));

    if (isPollingForDelivery(recentOrder, isDeliveryOrderCompleted)) {
      hasSeenCancelModal && setHasSeenCancelModal(false);
      startPolling(ORDER_PROGRESS_POLLING_DURATION_IN_MS);
    }

    const result = orderInProgress({
      recentOrder,
      curbsidePickupOrderId,
      serviceMode,
      isDeliveryOrderCompleted
    });
    if (result) {
      const handleNavArg: string = curbsidePickupOrderId || recentOrder?.rbiOrderId;
      const onButtonClick =
        recentOrder?.delivery?.status === DeliveryStatus.ORDER_CANCELLED
          ? openCancellationModal
          : handleNavigateToOrderConfirmation(handleNavArg);

      return {
        ...result,
        heading: formatMessage({ id: result.headingId }),
        details: formatMessage({ id: result.detailsId }),
        buttonContentText: formatMessage({ id: result.buttonContentTextId }),
        onButtonClick,
        onBottomServiceClick: onButtonClick,
        CancellationModal
      };
    }
  }

  stopPolling();
  if (store._id) {
    if ((!store.hasMobileOrdering || !enableOrdering) && store.physicalAddress?.address1) {
      return {
        heading: formatMessage({ id: 'yourSelectedStore' }),
        details: store.physicalAddress.address1,
        icon: <Icon icon="restaurant" color="white" width="1.75rem" height="1.75rem" />,
        buttonContentText: '',
        buttonContentIcon: null,
        onButtonClick: handleCartClick,
        onBottomServiceClick: handleServiceModeChange,
        buttonDisabled: !canUserCheckout,
        variant: BottomServiceVariants.DEFAULT,
        CancellationModal
      };
    }
    switch (serviceMode) {
      case ServiceMode.DELIVERY:
        if (deliveryAddress) {
          return {
            heading: formatMessage({ id: 'delivery' }),
            details: deliveryAddress.addressLine1,
            icon: <Icon icon="delivery" color="white" width="1.75rem" height="1.75rem" />,
            buttonContentText: cartComponent,
            buttonContentIcon: <Icon icon="bag" color="primary" />,
            onButtonClick: handleCartClick,
            onBottomServiceClick: handleServiceModeChange,
            buttonDisabled: !canUserCheckout,
            variant: BottomServiceVariants.DEFAULT,
            CancellationModal
          };
        }
        break;
      case ServiceMode.TAKEOUT:
      case ServiceMode.CURBSIDE:
      case ServiceMode.DRIVE_THRU:
      case ServiceMode.EAT_IN:
        if (store.physicalAddress?.address1) {
          return {
            heading: formatMessage({ id: 'pickUp' }),
            details: store.physicalAddress.address1,
            icon: <Icon icon="restaurant" color="white" width="1.75rem" height="1.75rem" />,
            buttonContentText: cartComponent,
            buttonContentIcon: <Icon icon="bag" color="primary" />,
            onButtonClick: handleCartClick,
            onBottomServiceClick: handleServiceModeChange,
            buttonDisabled: !canUserCheckout,
            variant: BottomServiceVariants.DEFAULT,
            CancellationModal
          };
        }
        break;
      default:
    }
  }
  return {
    heading: formatMessage({ id: 'forItemAvailability' }),
    details: formatMessage({ id: 'chooseALocation' }),
    icon: <Icon icon="markerFilled" color="white" width="1.75rem" height="1.75rem" />,
    buttonContentText: '0',
    buttonContentIcon: <Icon icon="bag" color="black" />,
    onButtonClick: handleCartClick,
    onBottomServiceClick: handleServiceModeChange,
    variant: BottomServiceVariants.NO_STORE,
    CancellationModal
  };
};
